import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// Show a "Keep working" toast 10 minutes before the access token expires.
// Clicking the action calls /api/auth/refresh and resets the timer.
const WARN_BEFORE_MS = 10 * 60 * 1000;

export default function SessionBanner() {
  const { user, expiresAt, refreshSession } = useAuth();
  const warnTimerRef = useRef(null);
  const dismissedForRef = useRef(null);
  const busyRef = useRef(false);
  const [, forceRender] = useState(0);

  const setBusy = useCallback((v) => {
    busyRef.current = v;
    forceRender((n) => n + 1);
  }, []);

  const showWarning = useCallback(() => {
    if (dismissedForRef.current === expiresAt) return;
    dismissedForRef.current = expiresAt;
    toast(
      "Your session expires in 10 minutes.",
      {
        description: "Want to stay signed in?",
        duration: 9 * 60 * 1000, // keep visible up to ~9 min
        id: "session-expiry-warning",
        action: {
          label: busyRef.current ? "Working…" : "Keep working",
          onClick: async () => {
            setBusy(true);
            try {
              await refreshSession();
              toast.success("Session extended");
            } catch (err) {
              console.warn("Session refresh failed:", err);
              toast.error("Couldn't extend session. Please sign in again.");
            } finally {
              setBusy(false);
            }
          },
        },
      }
    );
  }, [expiresAt, refreshSession, setBusy]);

  useEffect(() => {
    if (warnTimerRef.current) {
      clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
    if (!user || !expiresAt) return;

    const expiryMs = new Date(expiresAt).getTime();
    if (Number.isNaN(expiryMs)) return;
    if (dismissedForRef.current === expiresAt) return;

    const fireIn = expiryMs - Date.now() - WARN_BEFORE_MS;
    if (fireIn <= 0) {
      showWarning();
    } else {
      warnTimerRef.current = setTimeout(showWarning, fireIn);
    }
    return () => {
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    };
  }, [user, expiresAt, showWarning]);

  return null;
}
